import {
  MAINTENANCE_ACTIVITY_RENEW_SECONDS,
  type MaintenanceActivityRequest, type MaintenanceActivityReply, type MaintenanceActivityPermit,
} from '@playcastdotio/Messaging';

interface ActivityClient {
  request(request: MaintenanceActivityRequest): Promise<MaintenanceActivityReply>;
  dispose?(): void;
}
interface AdmissionOptions {
  onUncertain(peerId: string, reason: string): Promise<void>;
  clock?: () => number;
}
type OwnedPeer = { permit: MaintenanceActivityPermit; timer?: ReturnType<typeof setTimeout> };

/** Owns a native permit for the lifetime of each actual peer, including ICE repair. */
export class PeerMaintenanceAdmissionManager {
  private readonly peers = new Map<string, OwnedPeer>();
  private readonly pending = new Map<string, Promise<MaintenanceActivityPermit>>();
  private readonly releasing = new Map<string, Promise<void>>();
  private readonly closing = new Set<string>();
  private readonly uncertain = new Set<string>();
  private readonly clock: () => number;
  private disposed = false;

  constructor(private readonly activity: ActivityClient, private readonly options: AdmissionOptions) {
    this.clock = options.clock ?? Date.now;
  }

  acquire(peerId: string): Promise<MaintenanceActivityPermit> {
    if (this.disposed || this.closing.has(peerId)) return Promise.reject(new Error('maintenance_activity_closed'));
    if (!peerId || peerId.length > 128) return Promise.reject(new Error('maintenance_activity_owner_invalid'));
    const inFlight = this.pending.get(peerId);
    if (inFlight) return inFlight;
    const owned = this.peers.get(peerId);
    if (owned) return this.clock() < Date.parse(owned.permit.renewByUtc)
      ? Promise.resolve(owned.permit) : Promise.reject(new Error('maintenance_activity_expired'));
    const admission = this.reserve(peerId).then(permit => {
      const peer = { permit };
      this.peers.set(peerId, peer);
      this.uncertain.delete(peerId);
      if (this.disposed || this.closing.has(peerId)) throw new Error('maintenance_activity_closed');
      this.schedule(peerId, peer);
      return permit;
    }).finally(() => { this.pending.delete(peerId); });
    this.pending.set(peerId, admission);
    return admission;
  }

  /** The caller has already closed the connection or completed creation-failure cleanup. */
  release(peerId: string): Promise<void> {
    const pending = this.releasing.get(peerId);
    if (pending) return pending;
    const operation = this.releaseOwned(peerId).finally(() => { this.releasing.delete(peerId); });
    this.releasing.set(peerId, operation);
    return operation;
  }

  private async releaseOwned(peerId: string): Promise<void> {
    this.closing.add(peerId);
    try { await this.pending.get(peerId); } catch { /* Recover an uncertain reserve below. */ }
    let peer = this.peers.get(peerId);
    if (!peer && this.uncertain.has(peerId)) {
      peer = { permit: await this.reserve(peerId) };
      this.peers.set(peerId, peer);
    }
    if (peer) {
      clearTimeout(peer.timer);
      const response = await this.activity.request(this.ownedRequest('release', peer.permit));
      this.accept(response, peer.permit, false);
      if (this.peers.get(peerId) === peer) this.peers.delete(peerId);
    }
    this.uncertain.delete(peerId);
    this.closing.delete(peerId);
  }

  /** Invoke after real peer teardown; failed cleanup remains explicit and conservatively held. */
  async dispose(): Promise<void> {
    this.disposed = true;
    for (const peer of this.peers.values()) clearTimeout(peer.timer);
    const ids = new Set([...this.peers.keys(), ...this.pending.keys(), ...this.uncertain]);
    let failed = false;
    for (const peerId of ids) {
      try { await this.release(peerId); } catch { failed = true; }
    }
    if (failed) throw new Error('maintenance_activity_cleanup_incomplete');
    this.activity.dispose?.();
  }

  private async reserve(peerId: string): Promise<MaintenanceActivityPermit> {
    let response: MaintenanceActivityReply;
    try {
      response = await this.activity.request({ protocolVersion: 1, action: 'reserve', kind: 'player-session', ownerId: peerId });
    } catch (error) {
      this.uncertain.add(peerId);
      throw error;
    }
    try {
      if (response.permit && (response.permit.ownerId !== peerId || response.permit.kind !== 'player-session')) {
        throw new Error('maintenance_activity_owner_mismatch');
      }
      return this.accept(response);
    } catch (error) { if (response.admitted) this.uncertain.add(peerId); throw error; }
  }

  private accept(reply: MaintenanceActivityReply, previous?: MaintenanceActivityPermit, requireFresh = true): MaintenanceActivityPermit {
    if (!reply.admitted || !reply.permit) throw new Error(reply.errorCode ?? 'maintenance_activity_unavailable');
    const permit = reply.permit;
    if (!Number.isFinite(Date.parse(permit.renewByUtc)) || (requireFresh && Date.parse(permit.renewByUtc) <= this.clock() + 1000)) {
      throw new Error('maintenance_activity_expired');
    }
    if (previous && (permit.permitId !== previous.permitId || permit.ownerId !== previous.ownerId
      || permit.fence !== previous.fence || permit.nativeGeneration !== previous.nativeGeneration
      || permit.ownerProcessId !== previous.ownerProcessId || permit.ownerProcessCreatedUtc !== previous.ownerProcessCreatedUtc
      || permit.bootId !== previous.bootId)) throw new Error('maintenance_activity_owner_mismatch');
    return permit;
  }

  private ownedRequest(action: 'renew' | 'release', permit: MaintenanceActivityPermit): MaintenanceActivityRequest {
    return { protocolVersion: 1, action, kind: 'player-session', ownerId: permit.ownerId,
      permitId: permit.permitId, fence: permit.fence };
  }

  private schedule(peerId: string, peer: OwnedPeer): void {
    clearTimeout(peer.timer);
    const delay = Math.min(MAINTENANCE_ACTIVITY_RENEW_SECONDS * 1000,
      Math.max(0, Date.parse(peer.permit.renewByUtc) - this.clock() - 5_000));
    peer.timer = setTimeout(() => { void this.renew(peerId, peer); }, delay);
  }

  private async renew(peerId: string, peer: OwnedPeer): Promise<void> {
    if (this.disposed || this.closing.has(peerId) || this.peers.get(peerId) !== peer) return;
    try {
      const reply = await this.activity.request(this.ownedRequest('renew', peer.permit));
      peer.permit = this.accept(reply, peer.permit);
      if (!this.closing.has(peerId) && !this.disposed && this.peers.get(peerId) === peer) this.schedule(peerId, peer);
    } catch (error) {
      if (this.peers.get(peerId) !== peer) return;
      this.closing.add(peerId);
      try {
        await this.options.onUncertain(peerId, error instanceof Error ? error.message : 'maintenance_activity_unknown');
        await this.release(peerId);
      } catch { /* Keep ownership held until real teardown/cleanup can be confirmed. */ }
    }
  }
}
